import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { compressImageForUpload, isImageFile } from '../utils/imageUpload';
import PhotoGallery from './PhotoGallery';
import TrainingGroupPanel from './TrainingGroupPanel';
import './TrainingTracker.css';

const PILLAR_LABELS = {
  STRENGTH: 'Strength',
  STAMINA: 'Stamina',
  SKILL: 'Skill',
};

const STATUS_OPTIONS = [
  { value: 'DONE', label: 'Done' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'PENDING', label: 'Pending' },
];

const formatDay = (dateString) => {
  if (!dateString) return '';
  return new Date(`${dateString}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatLongDay = (dateString) => {
  if (!dateString) return '';
  return new Date(`${dateString}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

const buildEntriesFromToday = (todayData) => {
  if (todayData?.log?.entries?.length) {
    return todayData.log.entries.map((entry) => ({
      activityId: entry.activityId || '',
      pillar: entry.pillar,
      title: entry.title,
      status: entry.status || 'PENDING',
      actualDurationMin: entry.actualDurationMin ?? '',
      perceivedEffort: entry.perceivedEffort ?? '',
      notes: entry.notes || '',
    }));
  }

  return (todayData?.plannedActivities || []).map((activity) => ({
    activityId: activity.id,
    pillar: activity.pillar,
    title: activity.title,
    status: 'PENDING',
    actualDurationMin: activity.durationMin ?? '',
    perceivedEffort: '',
    notes: '',
  }));
};

const TrainingTracker = () => {
  const { user } = useAuth();
  const userId = user?.id;

  const [tab, setTab] = useState('today');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [todayData, setTodayData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [progress, setProgress] = useState(null);
  const [entries, setEntries] = useState([]);
  const [dayNotes, setDayNotes] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('BEGINNER');

  const loadAll = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError('');
      const [today, templateList, progressData] = await Promise.all([
        api.getTrainingToday(userId),
        api.getTrainingTemplates(),
        api.getTrainingProgress(userId),
      ]);
      setTodayData(today);
      setTemplates(templateList || []);
      setProgress(progressData);
      setEntries(buildEntriesFromToday(today));
      setDayNotes(today?.log?.notes || '');
      if (!today?.plan) {
        setTab((current) => (current === 'today' ? 'plan' : current));
      }
    } catch (err) {
      setError(err.message || 'Failed to load training tracker');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const hasPlan = Boolean(todayData?.plan);
  const log = todayData?.log;

  const completionLabel = useMemo(() => {
    if (!entries.length) return 'No activities scheduled';
    const done = entries.filter((e) => e.status === 'DONE' || e.status === 'PARTIAL').length;
    return `${done}/${entries.length} completed`;
  }, [entries]);

  const handleCreatePlan = async () => {
    if (!userId || !selectedTemplate) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await api.createTrainingPlan(userId, {
        templateId: selectedTemplate,
        startDate: new Date().toISOString().slice(0, 10),
      });
      setSuccess('Weekly plan created. Time to train.');
      setTab('today');
      await loadAll();
    } catch (err) {
      setError(err.message || 'Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  const updateEntry = (index, patch) => {
    setEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  };

  const handleSaveLog = async () => {
    if (!userId) return;
    if (!entries.length) {
      setError('No activities to log for today. Start a plan first.');
      setTab('plan');
      return;
    }
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const saved = await api.upsertTrainingLog(userId, {
        date: todayData?.date || new Date().toISOString().slice(0, 10),
        planId: todayData?.plan?.id,
        notes: dayNotes,
        entries: entries.map((entry) => ({
          activityId: entry.activityId || undefined,
          pillar: entry.pillar,
          title: entry.title,
          status: entry.status,
          actualDurationMin: entry.actualDurationMin === '' ? null : Number(entry.actualDurationMin),
          perceivedEffort: entry.perceivedEffort === '' ? null : Number(entry.perceivedEffort),
          notes: entry.notes || null,
        })),
      });
      setTodayData((prev) => ({ ...prev, log: saved }));
      setEntries(buildEntriesFromToday({ ...todayData, log: saved, plannedActivities: todayData?.plannedActivities }));
      setSuccess('Training log saved.');
      const progressData = await api.getTrainingProgress(userId);
      setProgress(progressData);
    } catch (err) {
      setError(err.message || 'Failed to save training log');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    let logId = todayData?.log?.id;
    if (!logId) {
      try {
        setUploading(true);
        const saved = await api.upsertTrainingLog(userId, {
          date: todayData?.date || new Date().toISOString().slice(0, 10),
          planId: todayData?.plan?.id,
          notes: dayNotes,
          entries: entries.map((entry) => ({
            activityId: entry.activityId || undefined,
            pillar: entry.pillar,
            title: entry.title,
            status: entry.status,
            actualDurationMin: entry.actualDurationMin === '' ? null : Number(entry.actualDurationMin),
            perceivedEffort: entry.perceivedEffort === '' ? null : Number(entry.perceivedEffort),
            notes: entry.notes || null,
          })),
        });
        logId = saved.id;
        setTodayData((prev) => ({ ...prev, log: saved }));
      } catch (err) {
        setError(err.message || 'Save the log before uploading photos');
        setUploading(false);
        return;
      }
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');
      const prepared = [];
      for (const file of files.slice(0, 5)) {
        if (!isImageFile(file)) {
          throw new Error('Please select image files only (JPG, PNG, GIF, or WebP).');
        }
        prepared.push(await compressImageForUpload(file));
      }
      const updated = await api.uploadTrainingPhotos(userId, logId, prepared);
      setTodayData((prev) => ({ ...prev, log: updated }));
      setSuccess('Photos uploaded.');
    } catch (err) {
      setError(err.message || 'Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const handleShare = async (visibility) => {
    if (!userId) return;
    let logId = todayData?.log?.id;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      if (!logId) {
        const saved = await api.upsertTrainingLog(userId, {
          date: todayData?.date || new Date().toISOString().slice(0, 10),
          planId: todayData?.plan?.id,
          notes: dayNotes,
          visibility,
          entries: entries.map((entry) => ({
            activityId: entry.activityId || undefined,
            pillar: entry.pillar,
            title: entry.title,
            status: entry.status,
            actualDurationMin: entry.actualDurationMin === '' ? null : Number(entry.actualDurationMin),
            perceivedEffort: entry.perceivedEffort === '' ? null : Number(entry.perceivedEffort),
            notes: entry.notes || null,
          })),
        });
        logId = saved.id;
        setTodayData((prev) => ({ ...prev, log: saved }));
      }
      const updated = await api.updateTrainingShare(userId, logId, visibility);
      setTodayData((prev) => ({ ...prev, log: updated }));
      if (visibility === 'PUBLIC' && updated.shareUrl) {
        try {
          await navigator.clipboard.writeText(updated.shareUrl);
          setSuccess('Public link copied to clipboard.');
        } catch {
          setSuccess(`Public link ready: ${updated.shareUrl}`);
        }
      } else if (visibility === 'CLUB') {
        setSuccess('Training log shared with your club.');
      } else {
        setSuccess('Training log set to private.');
      }
    } catch (err) {
      setError(err.message || 'Failed to update sharing');
    } finally {
      setSaving(false);
    }
  };

  const handleWhatsAppShare = () => {
    if (!log?.shareToken || log.visibility !== 'PUBLIC') {
      setError('Make the log public before sharing.');
      return;
    }
    const url = api.shareTrainingLogViaWhatsApp({
      athleteFirstName: user?.name?.split(' ')[0],
      date: log.date,
      completedCount: log.completedCount,
      totalCount: log.totalCount,
      shareToken: log.shareToken,
    });
    window.open(url, '_blank');
  };

  if (!userId) {
    return (
      <div className="training-tracker">
        <div className="training-tracker__empty card">
          <h1>Training</h1>
          <p>Sign in to plan strength, stamina, and skill sessions.</p>
          <Link to="/login" className="btn btn-primary">
            Log in
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="training-tracker loading">Loading training tracker…</div>;
  }

  return (
    <div className="training-tracker">
      <header className="training-tracker__hero">
        <p className="training-tracker__eyebrow">Daily Sports Life · Training</p>
        <h1>Training</h1>
        <p className="training-tracker__tagline">
          Build match stamina with daily strength, stamina, and skill work — then log your progress.
        </p>
        <div className="training-tracker__stats">
          <div>
            <strong>{todayData?.currentStreak ?? 0}</strong>
            <span>day streak</span>
          </div>
          <div>
            <strong>
              {todayData?.weekCompleted ?? 0}/{todayData?.weekTotal ?? 0}
            </strong>
            <span>this week</span>
          </div>
          <div>
            <strong>{completionLabel}</strong>
            <span>today</span>
          </div>
        </div>
      </header>

      <nav className="training-tracker__tabs" aria-label="Training sections">
        <button type="button" className={tab === 'today' ? 'is-active' : ''} onClick={() => setTab('today')}>
          Today
        </button>
        <button type="button" className={tab === 'plan' ? 'is-active' : ''} onClick={() => setTab('plan')}>
          Plan
        </button>
        <button type="button" className={tab === 'progress' ? 'is-active' : ''} onClick={() => setTab('progress')}>
          Progress
        </button>
        <button type="button" className={tab === 'group' ? 'is-active' : ''} onClick={() => setTab('group')}>
          Group
        </button>
      </nav>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {tab === 'today' && (
        <section className="training-panel" aria-label="Today's training">
          <div className="training-panel__header">
            <div>
              <h2>{formatLongDay(todayData?.date)}</h2>
              <p>
                {hasPlan
                  ? todayData.plan.title
                  : 'No active plan yet — choose a template to unlock today\'s session.'}
              </p>
            </div>
            {!hasPlan && (
              <button type="button" className="btn btn-primary" onClick={() => setTab('plan')}>
                Choose a plan
              </button>
            )}
          </div>

          {!entries.length && (
            <p className="training-muted">
              {hasPlan
                ? 'Rest day or plan ended — start a new weekly plan anytime.'
                : 'Pick a beginner, intermediate, or match-week plan to begin.'}
            </p>
          )}

          <div className="training-activity-list">
            {entries.map((entry, index) => (
              <article key={`${entry.activityId || entry.title}-${index}`} className="training-activity">
                <div className="training-activity__top">
                  <span className={`training-pillar training-pillar--${(entry.pillar || '').toLowerCase()}`}>
                    {PILLAR_LABELS[entry.pillar] || entry.pillar}
                  </span>
                  <h3>{entry.title}</h3>
                </div>

                {todayData?.plannedActivities?.[index]?.target && (
                  <p className="training-activity__target">{todayData.plannedActivities[index].target}</p>
                )}

                <div className="training-activity__controls">
                  <label>
                    Status
                    <select
                      value={entry.status}
                      onChange={(e) => updateEntry(index, { status: e.target.value })}
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Minutes
                    <input
                      type="number"
                      min="0"
                      value={entry.actualDurationMin}
                      onChange={(e) => updateEntry(index, { actualDurationMin: e.target.value })}
                    />
                  </label>
                  <label>
                    Effort (1–10)
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={entry.perceivedEffort}
                      onChange={(e) => updateEntry(index, { perceivedEffort: e.target.value })}
                    />
                  </label>
                </div>
                <label className="training-activity__notes">
                  Notes
                  <input
                    type="text"
                    value={entry.notes}
                    placeholder="How did it feel?"
                    onChange={(e) => updateEntry(index, { notes: e.target.value })}
                  />
                </label>
              </article>
            ))}
          </div>

          {entries.length > 0 && (
            <>
              <label className="training-day-notes">
                Day notes
                <textarea
                  rows="3"
                  value={dayNotes}
                  onChange={(e) => setDayNotes(e.target.value)}
                  placeholder="Optional summary for the day"
                />
              </label>

              <div className="training-actions">
                <button type="button" className="btn btn-primary" onClick={handleSaveLog} disabled={saving}>
                  {saving ? 'Saving…' : 'Save progress'}
                </button>
                <label className="btn btn-secondary training-upload-btn">
                  {uploading ? 'Uploading…' : 'Upload photos'}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    disabled={uploading}
                    onChange={handlePhotoUpload}
                  />
                </label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleShare('CLUB')}
                  disabled={saving || !todayData?.plan?.clubId}
                  title={!todayData?.plan?.clubId ? 'Join a club challenge first' : undefined}
                >
                  Share with club
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleShare('PUBLIC')}
                  disabled={saving}
                >
                  Share publicly
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleShare('PRIVATE')}
                  disabled={saving}
                >
                  Make private
                </button>
                {log?.visibility === 'PUBLIC' && log?.shareToken && (
                  <button type="button" className="btn btn-secondary" onClick={handleWhatsAppShare}>
                    WhatsApp
                  </button>
                )}
              </div>

              {log?.shareUrl && log?.visibility === 'PUBLIC' && (
                <p className="training-share-url">
                  Public link:{' '}
                  <a href={log.shareUrl} target="_blank" rel="noreferrer">
                    {log.shareUrl}
                  </a>
                </p>
              )}

              {log?.photoUrls?.length > 0 && (
                <div className="training-photos">
                  <h3>Session photos</h3>
                  <PhotoGallery photos={log.photoUrls} />
                </div>
              )}
            </>
          )}
        </section>
      )}

      {tab === 'plan' && (
        <section className="training-panel" aria-label="Training plan">
          <div className="training-panel__header">
            <div>
              <h2>Weekly plan</h2>
              <p>Start with a template. You can replace your active plan anytime.</p>
            </div>
          </div>

          <div className="training-template-grid">
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                className={`training-template ${selectedTemplate === template.id ? 'is-selected' : ''}`}
                onClick={() => setSelectedTemplate(template.id)}
              >
                <span className="training-template__level">{template.level}</span>
                <strong>{template.name}</strong>
                <p>{template.description}</p>
                <span className="training-template__meta">{template.days}-day mix of strength, stamina & skill</span>
              </button>
            ))}
          </div>

          <div className="training-actions">
            <button type="button" className="btn btn-primary" onClick={handleCreatePlan} disabled={saving}>
              {saving ? 'Creating…' : hasPlan ? 'Replace active plan' : 'Start this plan'}
            </button>
          </div>

          {todayData?.plan && (
            <div className="training-week">
              <h3>Current plan: {todayData.plan.title}</h3>
              <p className="training-muted">
                {formatDay(todayData.plan.startDate)} – {formatDay(todayData.plan.endDate)}
              </p>
              <div className="training-week__days">
                {(todayData.plan.days || []).map((day) => (
                  <div key={day.date} className="training-week__day">
                    <h4>{formatDay(day.date)}</h4>
                    <ul>
                      {(day.activities || []).map((activity) => (
                        <li key={activity.id}>
                          <span className={`training-pillar training-pillar--${activity.pillar.toLowerCase()}`}>
                            {PILLAR_LABELS[activity.pillar]}
                          </span>
                          <div>
                            <strong>{activity.title}</strong>
                            <p>{activity.target}</p>
                            <small>
                              {activity.durationMin} min · {activity.intensity?.toLowerCase()}
                            </small>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {tab === 'progress' && (
        <section className="training-panel" aria-label="Training progress">
          <div className="training-panel__header">
            <div>
              <h2>Progress</h2>
              <p>Streaks and recent check-ins from your training logs.</p>
            </div>
          </div>

          <div className="training-progress-cards">
            <div>
              <strong>{progress?.currentStreak ?? 0}</strong>
              <span>Current streak</span>
            </div>
            <div>
              <strong>{progress?.longestStreak ?? 0}</strong>
              <span>Longest streak</span>
            </div>
            <div>
              <strong>{Math.round(progress?.weekCompletionPercent || 0)}%</strong>
              <span>Week complete</span>
            </div>
          </div>

          <div className="training-history">
            <h3>Recent days</h3>
            {!progress?.recentDays?.length && (
              <p className="training-muted">No logs yet. Complete today&apos;s session to start your history.</p>
            )}
            <ul>
              {(progress?.recentDays || []).map((day) => (
                <li key={day.date}>
                  <div>
                    <strong>{formatDay(day.date)}</strong>
                    <span>
                      {day.completedCount}/{day.totalCount} activities
                      {day.hasPhotos ? ' · photos' : ''}
                      {day.visibility === 'PUBLIC' ? ' · public' : ''}
                      {day.visibility === 'CLUB' ? ' · club' : ''}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <p className="training-muted">
            Open the Group tab to join club challenges, compete on the leaderboard, and share sessions with teammates.
          </p>
        </section>
      )}

      {tab === 'group' && (
        <TrainingGroupPanel userId={userId} activePlan={todayData?.plan} onPlanChanged={loadAll} />
      )}
    </div>
  );
};

export default TrainingTracker;
